/**
 * `clock-sync.ts` wired to the page: `localStorage`, `Intl`, and the moments
 * worth asking — the mount, and every return to the foreground.
 *
 * Mounted ONCE, inside the session gate (`app/shell.tsx`, beside
 * `PushBridge`), so it only ever runs with a credential.
 *
 * ── ON RESUME, NOT ONLY ON MOUNT ────────────────────────────────────────────
 * The app that has to report a new zone is the one that was in a pocket on a
 * plane: `onAppActiveChange` is the app coming back, native or web, and on
 * the web it is `visibilitychange` (`native/app.ts`). A resume that changed
 * nothing costs two cache reads and no request.
 *
 * ── THE TWO KEYS ARE PER ORIGIN, AND THAT IS FINE ───────────────────────────
 * Live reload (`http://…:5191`) and the bundled shell (`https://localhost`)
 * are different origins, so each syncs once. An idempotent `204` and one GET
 * is the whole cost of that.
 *
 * Every read and write of `localStorage` is guarded: it throws in a private
 * window and comes back empty with site data cleared. A cache that answers
 * `null` sends one request too many, which is the correct way to fail.
 */
import { type ReactNode, useEffect } from "react";
import { useApi } from "../../data/api";
import { onAppActiveChange } from "../../native";
import {
  CLOCK_OFFSET_KEY,
  CLOCK_ZONE_KEY,
  type ClockStore,
  syncClock,
} from "./clock-sync";

/**
 * The cache behind both halves of the report. Exported for its OWN test, which
 * drives `syncClock` through this object rather than a copy of its body — the
 * one thing {@link forgetClockSync} has to get right is that it clears the two
 * keys *this* store reads. `ClockBridge` below is still the module's surface,
 * and the caller `CLAUDE.md` asks for is right here in the file.
 */
export const clockStore: ClockStore = {
  read(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Nothing is cached, so the next launch asks again. Harmless.
    }
  },
};

/**
 * Forget what this DEVICE last reported, so the next launch reports it again.
 *
 * The two keys are per origin (above) and per device — they were never per
 * account, and until T144 nothing cleared them. So: A signs in and syncs, A
 * signs out, B signs in on the same phone, the cache still says the zone has
 * been sent, nothing is sent for B, `account.time_zone` stays null for them
 * and their streak weeks are counted in UTC (`D152`) — a wrong number on the
 * home screen with nothing red anywhere. `data/provider.tsx` registers this in
 * `ACCOUNT_DATA`, the one list of what belongs to an account.
 *
 * It removes, and sends NOTHING. A cleanup that also reported would write the
 * PREVIOUS account's zone under the NEW account's token, which is the exact
 * confusion it exists to remove; the launch that has B's credential does the
 * send, a moment later.
 *
 * Both keys go. The offset is what the traveller resync compares, and the one
 * cached here was reconciled against the previous account's stored reminder.
 *
 * A failed removal THROWS, like `forgetCheckinState`: `runAccountCleanup`
 * wraps every step, so it reports this one by name (`clock`) and still runs
 * the others. Swallowing it would leave a removal that silently did nothing,
 * which is the bug above all over again — unlike the failed READS in the store
 * up there, where "nothing cached" is a safe answer that sends one request too
 * many.
 */
export function forgetClockSync(): void {
  localStorage.removeItem(CLOCK_ZONE_KEY);
  localStorage.removeItem(CLOCK_OFFSET_KEY);
}

/**
 * The device's IANA zone. `Intl` is in every runtime this app ships to; the
 * guard is for the one that answers an empty string rather than throwing.
 */
function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * A failure is logged, not reported. An offline launch fails here every time,
 * and routing that to `app/error-reporting` would fill the client-error log
 * with the ordinary case. `console.warn` is what `pnpm observe logs` follows
 * on the device, and what `data/provider.tsx` already uses for exactly this
 * class of swallowed background failure.
 */
function warn(what: string, error: unknown): void {
  console.warn(`[clock] reporting the ${what} failed`, error);
}

function useClockSync(): void {
  const api = useApi();

  useEffect(() => {
    const run = () => {
      syncClock({
        api,
        now: () => new Date(),
        onFailure: warn,
        store: clockStore,
        zone: deviceTimeZone,
      }).catch(() => undefined);
    };

    run();
    return onAppActiveChange((active) => {
      if (active) {
        run();
      }
    });
  }, [api]);
}

/**
 * The mount, and the hook's ONLY caller — `app/shell.tsx` renders this one
 * element beside `PushBridge` and imports nothing else. The hook itself is
 * not exported: an exported hook with no caller is the export `CLAUDE.md`
 * warns about, where every tool reads green and nothing is wired. Imported
 * from this module rather than from `features/profile/index.tsx`, which is
 * the lazily-loaded overlay and would drag the whole profile screen into the
 * first chunk.
 */
export function ClockBridge(): ReactNode {
  useClockSync();
  return null;
}
